// src/features/patients/MedicalHistoryTabs.tsx
// One reusable "Diagnoses / Past procedures / Medications"
// module — a sub-tab switcher over components/PatientHistoryPanel.tsx,
// which already owns the data/edit logic for all three. Built so
// Consultation's History tab and Patient Records' own history view read
// from the exact same component instead of two independently-drifting
// layouts of the same three lists; Vitals and Pre-Consult keep rendering
// PatientHistoryPanel directly (their own compact three-column capture
// grid, mid-workflow, is a different job than browsing history).
import { useState } from 'react';
import PatientHistoryPanel, { type HistoryKind } from '@/components/PatientHistoryPanel';

const SUB_TABS: { id: HistoryKind; label: string }[] = [
  { id: 'diagnoses', label: '🩺 Diagnoses' },
  { id: 'procedures', label: '🏥 Past procedures' },
  { id: 'medications', label: '💊 Medications' },
];

export interface MedicalHistoryTabsProps {
  uhid: string | undefined;
  editable?: boolean;
  /** Lets a parent (e.g. after an AI history pull) force every sub-tab's
   * next mount to reload — same contract as PatientHistoryPanel's own prop. */
  refreshToken?: unknown;
  onChanged?: () => void;
  /** Which sub-tabs to offer (default: all three). Patient Records shows
   * Medications as its own top-level tab, so it passes the other two. */
  kinds?: readonly HistoryKind[];
}

export function MedicalHistoryTabs({ uhid, editable = true, refreshToken, onChanged, kinds }: MedicalHistoryTabsProps) {
  const tabs = kinds ? SUB_TABS.filter(t => kinds.includes(t.id)) : SUB_TABS;
  const [sub, setSub] = useState<HistoryKind>(tabs[0]?.id ?? 'diagnoses');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1 border-b border-line">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSub(t.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-[13px] font-medium transition-colors ${
              sub === t.id ? 'border-teal text-teal' : 'border-transparent text-ink-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {/* key forces a fresh PatientHistoryPanel per sub-tab rather than one
          instance whose `kind` prop changes underneath it — cheap (this
          panel's own load() is a single list call) and avoids carrying
          stale rows/adding-state across a switch. */}
      <PatientHistoryPanel key={sub} kind={sub} uhid={uhid} editable={editable} refreshToken={refreshToken} onChanged={onChanged} />
    </div>
  );
}

export default MedicalHistoryTabs;
